// src/components/Pentests/PentestList.js
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Plus, Trash2 } from 'lucide-react';
import { pentestsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { HeroStrip, PrimaryCTA, EmptyState } from '../common/Shinobi';
import ConfirmModal from '../common/ConfirmModal';
import PentestCreateModal from './PentestCreateModal';
import { StatusChip } from './PentestStatus';
import { parseServerDate } from '../../utils/time';

const TYPE_LABELS = {
  external: 'External',
  internal: 'Internal',
  third_party_external: '3rd-party External',
  third_party_internal: '3rd-party Internal',
};

const GRADE_COLOR = {
  A: 'text-shinobi-green',
  B: 'text-emerald-700',
  C: 'text-yellow-700',
  D: 'text-orange-700',
  F: 'text-red-600',
};

export default function PentestList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, pt: null });
  const [deleting, setDeleting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const { isPentester } = useAuth();

  const fetchList = () => {
    setLoading(true);
    pentestsAPI
      .list()
      .then((data) => {
        setItems(data || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchList();
  }, []);

  const onDeleteClick = (pt, e) => {
    e.stopPropagation();
    setConfirmDelete({ isOpen: true, pt });
  };

  const onDeleteConfirm = async () => {
    if (!confirmDelete.pt) return;
    setDeleting(true);
    try {
      await pentestsAPI.remove(confirmDelete.pt.id);
      setConfirmDelete({ isOpen: false, pt: null });
      fetchList();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="p-8 text-shinobi-green">Loading penetration tests…</div>;
  if (error) return <div className="p-8 text-red-600">Error: {error}</div>;

  const empty = items.length === 0;

  return (
    <div className="space-y-6">
      <HeroStrip
        icon={ClipboardCheck}
        accent="green"
        eyebrow="Engagements"
        title="Penetration Tests"
        subtitle="All Wasabi Red Team engagements. Each test owns its tools, findings, AI insights, and reports."
        actions={
          isPentester && (
            <PrimaryCTA
              variant="green"
              icon={Plus}
              iconPosition="left"
              onClick={() => setCreateOpen(true)}
            >
              New Pen Test
            </PrimaryCTA>
          )
        }
      />

      <div className="px-6">
        {empty ? (
          <EmptyState
            icon={ClipboardCheck}
            accent="green"
            title="No pen tests yet"
            description={
              isPentester
                ? 'Create the first one to start scoping a Wasabi engagement.'
                : 'Once a Pentester creates an engagement, it will show up here.'
            }
            actionLabel={isPentester ? 'New Pen Test' : null}
            actionIcon={Plus}
            onAction={isPentester ? () => setCreateOpen(true) : null}
          />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60">
            <table className="w-full text-sm text-left text-slate-800">
              <thead className="bg-cyber-200/60 text-xs uppercase tracking-wider text-slate-700">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Grade</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Findings</th>
                  <th className="px-4 py-3">Retests</th>
                  <th className="px-4 py-3">Created</th>
                  {isPentester && <th className="px-4 py-3 w-12"></th>}
                </tr>
              </thead>
              <tbody>
                {items.map((pt) => (
                  <tr
                    key={pt.id}
                    onClick={() => navigate(`/pentests/${pt.id}/dashboards`)}
                    className="cursor-pointer border-t border-cyber-300/30 hover:bg-cyber-200/40"
                  >
                    <td className="px-4 py-3 font-medium">{pt.name}</td>
                    <td className="px-4 py-3 text-slate-700">{TYPE_LABELS[pt.type] || pt.type}</td>
                    <td className="px-4 py-3"><StatusChip status={pt.status} size="sm" /></td>
                    <td className={`px-4 py-3 font-bold text-lg ${GRADE_COLOR[pt.grade] || ''}`}>{pt.grade}</td>
                    <td className="px-4 py-3 tabular-nums">{pt.overall_score?.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <SeverityChips counts={pt.severity_counts} total={pt.finding_count} />
                    </td>
                    <td className="px-4 py-3 text-slate-700">{pt.retest_count}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      {(() => { const d = parseServerDate(pt.created_at); return d ? d.toLocaleDateString() : '-'; })()}
                    </td>
                    {isPentester && (
                      <td className="px-4 py-3 w-12">
                        <button
                          type="button"
                          onClick={(e) => onDeleteClick(pt, e)}
                          title="Delete pen test"
                          aria-label={`Delete ${pt.name}`}
                          className="p-1.5 rounded text-slate-500 hover:text-red-600 hover:bg-red-100 transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={() => !deleting && setConfirmDelete({ isOpen: false, pt: null })}
        onConfirm={onDeleteConfirm}
        title="Delete Penetration Test"
        message={
          confirmDelete.pt
            ? `Permanently delete "${confirmDelete.pt.name}"? This removes the pen test, its questionnaire, all findings, all linked tool runs, AI analyses, and reports. This action cannot be undone.`
            : ''
        }
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        variant="danger"
      />

      <PentestCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}

function SeverityChips({ counts, total }) {
  if (!total) return <span className="text-slate-500">–</span>;
  const { critical = 0, high = 0, medium = 0, low = 0, info = 0 } = counts || {};
  return (
    <div className="flex gap-1.5 text-xs">
      {critical > 0 && <Chip label={`${critical} C`} className="bg-red-100 text-red-700" />}
      {high > 0 && <Chip label={`${high} H`} className="bg-orange-100 text-orange-700" />}
      {medium > 0 && <Chip label={`${medium} M`} className="bg-yellow-100 text-yellow-700" />}
      {low > 0 && <Chip label={`${low} L`} className="bg-blue-100 text-blue-700" />}
      {info > 0 && <Chip label={`${info} I`} className="bg-slate-100 text-slate-700" />}
    </div>
  );
}

function Chip({ label, className }) {
  return <span className={`px-1.5 py-0.5 rounded font-mono ${className}`}>{label}</span>;
}
